// Imports for external libraries go here.
// Imports for external libraries go here.
import dynamic from 'next/dynamic';
import { FC, lazy, Suspense, useCallback } from 'react';
import clsx from 'clsx';

import { ResponsiveGrid } from '@adobe/aem-react-editable-components';

// Imports for internal (to the monorepo) libraries go here,
import { AEMReactCompMap, Text, Types, Heading } from '@marriott/mi-ui-library';
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { usePageContext } from '../../modules';
import { AccountPartnerCardWrapperProps } from './AccountPartnerCardWrapper.types';
import shuffle from 'lodash/shuffle';
import take from 'lodash/take';
import {
  StyledAccountPartnerCardWrapper,
  StyledAccountPartnerCardCompParent,
  StyledAccountPartnerCardChildComponentWrapper,
  StyledAccountPartnerCardHeaderWrapper,
  StyledAccountPartnerCardPublishedContainer,
} from './AccountPartnerCardWrapper.styles';

const AccountPartnerCard = dynamic(() => import('../PartnerCard/PartnerCard').then(res => res.PartnerCard));
const CarouselWrapperComponent = dynamic(() =>
  import('../../molecules/CarouselControls').then(res => res.CarouselWrapperComponent)
);

// Use named rather than default exports.
export const AccountPartnerCardWrapper: FC<AccountPartnerCardWrapperProps> = props => {
  /**
   * with data page component we need to author
   * Account partner card only
   * other wise it will not work
   */
  const {
    isAuthorMode,
    totalNumberOfCards,
    itemPath,
    pagePath,
    model,
    allowedComponents,
    wrapperId,
    subHeader,
    header,
    desktopColumns,
    mobileColumns,
    tabletColumns,
    carousel,
    randomized,
    selectPartnerListItems,
    styleClass,
    typeofPartner,
    hideCardDetails,
    fallBackComponentId,
    displayFeatureHorizontalCard,
    displayFeatureHorizontalCardCount,
  } = props;

  const isRandomized = Boolean(randomized);

  const AllContstant = 'all';
  const { currentLocale, datalayer } = usePageContext();
  // get the  mapping from allowed components in model json
  const mapper = AEMReactCompMap(allowedComponents);

  // Note: Some inner components are dynamically resolved and loosely typed

  const authorComponentWrapper = (index: number) => {
    /** create parsys container in author mode
     * used to render the container inside the author mode
     */
    return (
      <ResponsiveGrid
        key={`reponsivegrid-account-static-${index}`}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        pagePath={pagePath}
        columnCount="12"
        itemPath={`${itemPath}/${totalNumberOfCards[index]}`}
        config={{
          isEmpty: () => true,
          resourceType: `mi-aem-common-spa/components/container`,
        }}
      />
    );
  };

  const getPartnerCardIdForUserCountry = () => {
    /**
     * filter partner card list by user location country
     */
    return selectPartnerListItems?.filter(
      key => key.countryName?.includes(datalayer?.currentLocCountry) || key?.countryName?.includes(AllContstant)
    )?.[0]?.showCardForCountry;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getCompLoadedByItemsOrderForPartnerCard = (parsysItems: any, parsysItemsOrder: any) => {
    /**
     * filter the partner card by country
     * add fallback if no partner card for particular location
     */
    const userCountryPartnerCard = getPartnerCardIdForUserCountry();

    const order = parsysItemsOrder;

    return order?.flatMap((key: string) => {
      /**
       * loop on all the items present in the container
       */

      if (Object.prototype.hasOwnProperty.call(parsysItems, key)) {
        const itemProps = updateItemsProps(parsysItems[key]);
        const itemName = itemProps[':type']?.split('/').pop();

        if (Object.prototype.hasOwnProperty.call(mapper, itemName) && itemName === 'partnercard') {
          if (userCountryPartnerCard?.includes(itemProps.allPartnerType)) {
            // load all the components in data page comp
            return (
              <StyledAccountPartnerCardChildComponentWrapper
                className={generateClassNameAsPerTheViewPort()}
                key={`card-child-comp-wrapper-${key}-${currentLocale}`}
              >
                <AccountPartnerCard key={`child-comp-common-${key}-${currentLocale}`} {...itemProps} />
              </StyledAccountPartnerCardChildComponentWrapper>
            );
          }
          return null;
        }
        return null;
      }
      return null;
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getDataPageCompItemsByCountry = useCallback((compItems: any) => {
    // eslint-disable-next-line no-prototype-builtins
    if (compItems?.hasOwnProperty(':items')) {
      /**
       * check if items is present
       */
      const parsysItems = compItems[':items'];
      const parsysItemsOrderRaw = compItems[':itemsOrder']; // we might need to adjust order of display
      const parsysItemsOrder = computeItemsOrder(parsysItemsOrderRaw);

      return getCompLoadedByItemsOrderForPartnerCard(parsysItems, parsysItemsOrder);
    }
    return null;
  }, []);

  // Normalize itemsOrder handling in one place
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const computeItemsOrder = (orderRaw: any) =>
    Array.isArray(orderRaw) ? (isRandomized ? take(shuffle(orderRaw), 3) : orderRaw) : orderRaw;

  const generateClassNameAsPerTheViewPort = (itemName?: string) => {
    if (itemName && itemName === 'cardhorizontalfeature') {
      return 'col-12 mb-3 mb-xl-0';
    }

    /**
     * generate the viewport columns classes according to authoring
     */
    if (desktopColumns && !isNaN(parseInt(desktopColumns))) {
      const deskCol = desktopColumns
        ? Number.isInteger(12 / parseInt(desktopColumns))
          ? 12 / parseInt(desktopColumns)
          : null
        : 12;
      const tabletCol = tabletColumns
        ? Number.isInteger(12 / parseInt(tabletColumns))
          ? 12 / parseInt(tabletColumns)
          : null
        : 12;
      const mobileCol = mobileColumns
        ? Number.isInteger(12 / parseInt(mobileColumns))
          ? 12 / parseInt(mobileColumns)
          : null
        : 12;
      return carousel
        ? 'glide__slide col'
        : `col-xl${deskCol ? `-${deskCol}` : ''} col-md${tabletCol ? `-${tabletCol}` : ''} col${
            mobileCol ? `-${mobileCol}` : ''
          } mb-xl-0  mb-3`;
    }
    return carousel ? 'glide__slide col' : `col-12`;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateItemsProps = (dataProps: any) => {
    /**
     * update item props if required
     */
    const name = dataProps[':type']?.split('/').pop();
    switch (name) {
      case 'partnercard':
        dataProps.titleFontSizeClass = isNaN(parseInt(desktopColumns || ''))
          ? 't-subtitle-l'
          : parseInt(desktopColumns || '') > 2
          ? 't-subtitle-l'
          : 't-subtitle-xl';
        return dataProps;
      default:
        return dataProps;
    }
  };

  const getLazyLoadCommonComp = (itemName: string) => {
    /**
     * lazy load all common compoennts
     */
    const innerComp = itemName === 'cardhorizontalfeature' ? 'CardHorizontalFeature' : mapper[itemName];

    return lazy(() =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      import(`@marriott/mi-common-static-components`).then((mod: any) => ({
        /**
         * lazy load the dynamic components
         */
        default: mod[`${innerComp}`],
      }))
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getItemsFromParsys = useCallback((compItems: any) => {
    const hideCard = getGeoTargetedComponentIdsForUserCountry();
    const parsys = compItems?.parsys;
    // eslint-disable-next-line no-prototype-builtins
    if (parsys?.hasOwnProperty(':items')) {
      /**
       * check if items is present
       */
      const parsysItems = parsys[':items'];
      const parsysItemsOrderRaw = parsys[':itemsOrder'];
      const parsysItemsOrder = computeItemsOrder(parsysItemsOrderRaw);
      const userPartnerCard = getPartnerCardIdForUserCountry();

      const compArr = parsysItemsOrder?.flatMap((key: string) => {
        /**
         * loop on all the items present in the container
         */
        if (Object.prototype.hasOwnProperty.call(parsysItems, key)) {
          const itemProps = updateItemsProps(parsysItems[key]);
          const itemName = itemProps[':type']?.split('/').pop();
          if (Object.prototype.hasOwnProperty.call(mapper, itemName)) {
            /** import the dynamic common component */

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let Component: any;
            if (itemName === 'datapagecomponent') {
              /** import the dynamic account component */

              if (
                displayFeatureHorizontalCard &&
                displayFeatureHorizontalCardCount &&
                userPartnerCard?.length &&
                userPartnerCard?.length <= parseInt(displayFeatureHorizontalCardCount)
              ) {
                /**
                 * return null if we have feature horizontal card to be displayed
                 * and displayFeatureHorizontalCardCount is present
                 */
                return null;
              } else {
                return userPartnerCard?.length ? getDataPageCompItemsByCountry(itemProps) : null;
              }
            } else if (typeofPartner && fallBackComponentId && itemProps.componentId === fallBackComponentId) {
              /**
               * Render fallback component if there is no partner card
               * Fallback component should render when partner card length == 0
               */
              if (!userPartnerCard?.length) {
                Component = getLazyLoadCommonComp(itemName);
              } else {
                /**
                 * don not render fallback component if partner card is there
                 */
                return null;
              }
            } else {
              if (
                displayFeatureHorizontalCard &&
                userPartnerCard?.length &&
                userPartnerCard?.length > parseInt(displayFeatureHorizontalCardCount)
              ) {
                return null;
              } else {
                Component = getLazyLoadCommonComp(itemName);
              }
            }

            if (hideCard && hideCard.length && hideCard.includes(itemProps.componentId)) {
              return null;
            }
            return (
              <StyledAccountPartnerCardChildComponentWrapper
                className={generateClassNameAsPerTheViewPort(itemName)}
                key={`card-child-comp-wrapper-${key}-${currentLocale}`}
              >
                <Component key={`card-child-comp-wrapper-${key}-${currentLocale}`} {...itemProps} />
              </StyledAccountPartnerCardChildComponentWrapper>
            );
          }
          return null;
        }
        /** logs error at this place */
        return null;
      });
      return compArr;
    }
    return null;
  }, []);

  const getGeoTargetedComponentIdsForUserCountry = () => {
    /**
     * hide the complete lane id user is outside of designated country
     */
    const arr = hideCardDetails?.flatMap(key => {
      if (!key.countryName.includes(datalayer?.currentLocCountry)) {
        return [key.componentId];
      }
      return [];
    });
    return arr;
  };
  const isHideTheLane = () => {
    /**
     * hide the complete lane if partner card , and fallback component is empty
     *
     *
     */

    return typeofPartner && typeofPartner !== 'N/A' && !fallBackComponentId
      ? !getPartnerCardIdForUserCountry()?.length
      : false;
  };

  const RenderCompHeaderForTheSection = () => {
    return header || subHeader ? (
      <StyledAccountPartnerCardHeaderWrapper className="col-12 mb-4">
        {header && (
          <Heading
            variation={Types.headingType.title}
            fontSize={Types.size.small}
            element={Types.tags.h3}
            titleText={header}
            disableCustomClickTrack={true}
            customClass={'mb-1'}
          />
        )}
        {subHeader && (
          <Text customClass="" copyText={subHeader} fontSize={Types.size.medium} element={Types.tags.paragraph} />
        )}
      </StyledAccountPartnerCardHeaderWrapper>
    ) : (
      <></>
    );
  };

  const renderPublishedContent = () => {
    /**
     * render publish content on the basis of props passed to the wrapper
     */
    const childComponent = getItemsFromParsys(model?.cqItems);
    const displayArr = childComponent?.filter(Boolean);

    return isHideTheLane() || !displayArr?.length ? (
      <></>
    ) : (
      <StyledAccountPartnerCardWrapper
        id={wrapperId}
        className={clsx(
          styleClass?.includes('alternate') ? 'alternate' : '',
          styleClass?.includes('m-container-fullbleed') ? 'm-container-fullbleed' : ''
        )}
      >
        <StyledAccountPartnerCardPublishedContainer
          id={`${wrapperId}-container-id`}
          className={clsx(
            styleClass?.replace('alternate', '')?.replace('m-container-fullbleed', 'py-5') || '',

            'container'
          )}
        >
          <div className="row add-gutter-space-top">
            <RenderCompHeaderForTheSection />
          </div>
          <Suspense fallback={<></>}>
            {carousel && displayArr?.length > 1 ? (
              <CarouselWrapperComponent
                tabletColumns={tabletColumns}
                desktopColumns={desktopColumns}
                totalSlideLen={displayArr.length}
                carousel={carousel}
                wrapperId={wrapperId}
              >
                {displayArr}
              </CarouselWrapperComponent>
            ) : (
              <div className={clsx('row add-gutter-space-bottom')}>{displayArr}</div>
            )}
          </Suspense>
        </StyledAccountPartnerCardPublishedContainer>
      </StyledAccountPartnerCardWrapper>
    );
  };

  /**
   * return function components
   */
  return (
    <StyledAccountPartnerCardCompParent
      data-testid={`account-AccountPartnerCardWrapper-${wrapperId}`}
      data-component-name="o-account-AccountPartnerCardWrapper"
      key={`accountPartnerCard-static-wrapper-${wrapperId}`}
    >
      {/* render publish content for component */}
      {!isAuthorMode && renderPublishedContent()}
      {/* render authorable component */}
      {isAuthorMode && Array.from({ length: totalNumberOfCards?.length }, (_, i) => authorComponentWrapper(i))}
    </StyledAccountPartnerCardCompParent>
  );
};
